import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => `${Number(v) < 0 ? '−' : ''}$${Math.abs(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const bizDay = (back = 0) => {
    const ct = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Chicago' }));
    if (ct.getHours() < 7) ct.setDate(ct.getDate() - 1);
    ct.setDate(ct.getDate() - back);
    return `${ct.getFullYear()}-${String(ct.getMonth() + 1).padStart(2, '0')}-${String(ct.getDate()).padStart(2, '0')}`;
};
const RANGES = [['today', 'Today', 0], ['7d', 'Last 7 days', 6], ['30d', 'Last 30 days', 29]];
const TH = 'pb-2 text-right';

const DaysTable = ({ days }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm text-[#cfd6dc]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#A0AAB2]">
                <th className="pb-2">Day</th><th className={TH}>Loads</th><th className={TH}>Today's cash-outs paid</th><th className={TH}>Today's profit</th>
                <th className={TH}>Old cash-outs paid</th><th className={TH}>Total cash sent</th><th className={TH}>Still owed (asked that day)</th>
            </tr></thead>
            <tbody>{[...days].reverse().map((d) => (
                <tr key={d.day} className="border-t border-white/5" data-testid={`day-split-row-${d.day}`}>
                    <td className="py-1.5">{d.day}</td><td className="text-right tabular-nums">{fmt(d.loads_today)}</td>
                    <td className="text-right tabular-nums">{fmt(d.cashouts_today)}</td>
                    <td className={`text-right font-bold tabular-nums ${d.profit_today < 0 ? 'text-rose-300' : 'text-[#39FF14]'}`}>{fmt(d.profit_today)}</td>
                    <td className="text-right tabular-nums text-amber-200">{fmt(d.old_cashouts_paid)} <span className="text-white/40">({d.old_cashouts_count})</span></td>
                    <td className="text-right tabular-nums">{fmt(d.cash_sent_today)}</td>
                    <td className="text-right tabular-nums text-white/70">{fmt(d.owed_today)} <span className="text-white/40">({d.owed_today_count})</span></td>
                </tr>
            ))}</tbody>
        </table>
    </div>
);

const OldTable = ({ rows }) => (
    <div className="max-h-[360px] overflow-auto">
        <table className="w-full text-sm text-[#cfd6dc]">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-[#A0AAB2]">
                <th className="pb-2">Paid</th><th className="pb-2">Asked for</th><th className="pb-2">Player</th><th className="pb-2">Method</th><th className={TH}>Amount</th>
            </tr></thead>
            <tbody>{rows.map((r, i) => (
                <tr key={`${r.id}-${i}`} className="border-t border-white/5" data-testid="day-split-old-row">
                    <td className="py-1.5 text-xs">{new Date(r.at).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</td>
                    <td className="text-xs">{r.requested_at ? new Date(r.requested_at).toLocaleString('en-US', { timeZone: 'America/Chicago' }) : '—'} {r.days_waiting != null && <span className="text-white/40">({r.days_waiting}d)</span>}</td>
                    <td>{r.player}</td><td className="text-xs">{r.method}</td><td className="text-right tabular-nums text-amber-200">{fmt(r.amount)}</td>
                </tr>
            ))}</tbody>
        </table>
        {!rows.length && <p className="py-4 text-center text-white/50">No older cash-outs were paid in this period.</p>}
    </div>
);

export default function DailyProfitPanel() {
    const [range, setRange] = useState('7d');
    const [data, setData] = useState(null);
    const back = RANGES.find((r) => r[0] === range)[2];
    const q = `from_day=${bizDay(back)}&to_day=${bizDay()}`;
    useEffect(() => {
        setData(null);
        axios.get(`${API}/admin/finance/day-split?${q}`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ error: true }));
    }, [q]);
    return (
        <div className="space-y-4" data-testid="daily-profit-panel">
            <div className="flex flex-wrap items-center gap-2">
                <h2 className="mr-auto text-lg font-black text-white">Daily profit · old cash-outs kept apart</h2>
                {RANGES.map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setRange(id)} data-testid={`day-split-range-${id}`}
                        className={`rounded-full px-3 py-1 text-xs font-bold ${range === id ? 'bg-[#39FF14] text-black' : 'bg-white/5 text-white/70'}`}>{label}</button>
                ))}
                <a href={`${API}/admin/finance/day-split?${q}&format=csv`} className="inline-flex items-center gap-1 rounded-full bg-white/5 px-3 py-1 text-xs font-bold text-white/80" data-testid="day-split-csv"><Download className="h-3 w-3" /> Old cash-outs CSV</a>
            </div>
            <p className="text-xs text-[#A0AAB2]">{data?.rule}</p>
            {!data ? <Loader2 className="h-5 w-5 animate-spin text-white/50" /> : data.error ? <p className="text-rose-300">Couldn't load the report.</p> : (
                <>
                    <section className="rounded-xl border border-white/10 bg-black/30 p-4"><DaysTable days={data.days} /></section>
                    <section className="rounded-xl border border-white/10 bg-black/30 p-4">
                        <h3 className="mb-3 text-sm font-black uppercase tracking-wider text-amber-200">Older cash-outs paid in this period</h3>
                        <OldTable rows={data.old_cashouts} />
                    </section>
                </>
            )}
        </div>
    );
}
